import {
  agruparConversas,
  filtrarPorTitulo,
  indexarRotulosPorPersona,
} from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'
import { MODO_AGRUPAMENTO_PASTAS } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { formatConversationDate } from '@modules/aawz-intelligence-v2/utils/formatConversationDate'
import { CREDITOS } from '@modules/ai-framework/constants/copy'
import { formatBrokerCode } from '@utils/cliente-codigo/cliente-codigo'

/**
 * Seções do palette da IA.
 *
 * Lógica pura, como o resto de `utils/`: arrays entram, grupos saem. Nada de
 * `$store`, `$route` ou `$access` aqui — quem lê o mundo é o componente, que
 * fica burro de propósito e testável sem montar o modal.
 *
 * Formato de saída `[{ title, items }]` porque é o que o `decorateGroups` do
 * global-search consome — o mesmo que numera os itens para a navegação por
 * seta. `agruparConversas` fala PT (`titulo`/`itens`); a tradução mora aqui.
 */

export const SECAO_ACOES = 'Ações'
export const SECAO_AGENTES = 'Agentes'
export const SECAO_MODELO = 'Modelo'
export const SECAO_CONVERSAS = 'Conversas'
export const SECAO_CLIENTES = 'Clientes'

export const TIPO_ACAO = 'ia-acao'
export const TIPO_AGENTE = 'ia-agente'
export const TIPO_MODELO = 'ia-modelo'
export const TIPO_CONVERSA = 'ia-conversa'
export const TIPO_CLIENTE = 'ia-cliente'

// [design it.5 da 009, re-medido] A ÚNICA ação que NÃO sai do palette — troca
// o assunto dele para a lista de clientes (`ia-palette.vue::escoparEmClientes`)
// — e a ÚNICA ação que também trava por `estado.clienteTravado` (ver o item
// "Cliente em foco" logo abaixo). Exportada daqui (fonte única do `acao`) em
// vez de redigitada em cada consumidor: `ia-palette.vue` a usa para rotear o
// clique, `ia-palette-item.vue` para saber QUANDO mostrar o motivo do
// travamento — as duas precisam concordar com o mesmo literal.
export const ACAO_ESCOLHER_CLIENTE = 'escolher-cliente'
// Abre o modal de créditos (`MODAL.AI_FRAMEWORK_CREDITOS`) — o mesmo canal que o atalho
// do rodapé da lateral emite. Exportada pelo mesmo motivo de `ACAO_ESCOLHER_CLIENTE`:
// `ia-palette.vue` roteia o clique por este literal.
export const ACAO_MEUS_CREDITOS = 'meus-creditos'
// As demais ações roteadas pelo `ia-palette.vue` — fonte única do literal, como acima.
export const ACAO_NOVA_CONVERSA = 'nova-conversa'
export const ACAO_RACIOCINIO = 'raciocinio'
export const ACAO_ANEXAR_ARQUIVOS = 'anexar-arquivos'
export const ACAO_ANEXAR_CARTEIRA = 'anexar-carteira'
export const ACAO_BUSCAR_PLATAFORMA = 'buscar-plataforma'
// O ícone do item é o do rodapé e do card grande: quem chega aos créditos por qualquer
// das portas reconhece o mesmo objeto.
const ICONE_CREDITOS = 'wallet'

// Escopos de abertura: o palette pode nascer restrito a UMA seção, quando quem
// abriu foi o chip de agente ou o de cliente do composer.
export const ESCOPO_AGENTES = 'agentes'
export const ESCOPO_CLIENTES = 'clientes'
// ★ O modelo entrou como escopo em 27/08: na `/ia` o CHIP dele some da barra
// (cabe uma fileira só), e o comando `/modelo` ficava sem para onde abrir. O
// chip sumir é decisão de layout, não de produto — o modelo continua trocável.
export const ESCOPO_MODELOS = 'modelos'

/**
 * Nomes pelos quais cada seção é encontrada.
 *
 * Sem isto, digitar "modelo" não achava nada: o filtro só olhava rótulo,
 * descrição e aliases de cada ITEM, e o título da seção nunca entrava na busca —
 * quem procurava a seção via "Nenhum resultado" com ela listada logo abaixo.
 *
 * Plural e inglês porque é o que se digita sem pensar.
 */
const APELIDOS_DA_SECAO = {
  [SECAO_ACOES]: ['ações', 'acoes', 'ação', 'comandos', 'actions'],
  [SECAO_AGENTES]: ['agentes', 'agente', 'agents', 'agent'],
  [SECAO_MODELO]: ['modelo', 'modelos', 'model', 'models'],
  [SECAO_CONVERSAS]: ['conversas', 'conversa', 'histórico', 'chats'],
  [SECAO_CLIENTES]: ['clientes', 'cliente', 'clients', 'escopo'],
}

const CONVERSA_SEM_TITULO = 'Conversa sem título'
const ESCOPO_GERAL = 'Todo o escritório'
const ICONE_CLIENTE = 'user-2'

/**
 * Quantos clientes o palette renderiza na PRIMEIRA leva.
 *
 * Um escritório grande tem centenas, e cada linha é um nó de DOM com ícone,
 * título e descrição. Sem teto, abrir a lista travava a navegação por seta —
 * cada tecla re-renderizava tudo.
 *
 * ⚠️ NÃO é teto de busca nem de acesso: o filtro varre a lista inteira, e o
 * componente sobe o limite conforme se rola ou se navega até o fim (scroll
 * infinito). É o oposto do teto de conversas que este palette já teve, que
 * escondia dado sem caminho de volta.
 */
export const CLIENTES_POR_LEVA = 30
// Mesmo separador da meta da linha da lateral, para as duas lerem igual.
const SEPARADOR_META = ' · '
const ICONE_CONVERSA = 'message-text'
const ICONE_AGENTE_PADRAO = 'robot'
const ICONE_MODELO = 'ia-head'

const texto = (valor) => String(valor || '').trim()

const semAcento = (valor) =>
  String(valor || '')
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()

/**
 * Casa por TRECHO, e não por prefixo como o índice de páginas do global-search.
 *
 * Aqui a lista é curta e os rótulos são compostos ("Consultor Estratégico"),
 * onde só o prefixo faria "estrategico" não achar nada.
 */
const casaTexto = (termo, ...partes) => {
  const alvo = semAcento(termo)
  if (!alvo) return true
  return partes.some((parte) => semAcento(parte).includes(alvo))
}

/**
 * O termo nomeia a SEÇÃO inteira? Então ela passa sem filtrar item a item.
 *
 * Quem digita "modelos" quer a lista de modelos, não os modelos cujo nome contém
 * "modelos" — que é nenhum.
 */
const casaSecao = (termo, secao) =>
  Boolean(texto(termo)) && casaTexto(termo, ...(APELIDOS_DA_SECAO[secao] || []))

/**
 * Filtra os itens de uma seção, a menos que o termo nomeie a própria seção.
 *
 * @param {Array} itens
 * @param {String} termo
 * @param {String} secao um dos `SECAO_*`
 * @param {Function} casa `(item) => Boolean` — o teste por item
 */
const filtrarNaSecao = (itens, termo, secao, casa) => {
  if (casaSecao(termo, secao)) return itens
  return itens.filter(casa)
}

// ---------------------------------------------------------------------------
// Ações
// ---------------------------------------------------------------------------

/**
 * As ações do chat que não são "escolher uma coisa de uma lista".
 *
 * Cada uma carrega `aliases` em inglês porque quem vem de outro chat digita
 * `new`, `clear`, `search` — o mesmo contrato dos comandos da `/` no composer.
 */
export function secaoDeAcoes(config) {
  const estado = config.estado || {}
  const itens = [
    {
      acao: ACAO_NOVA_CONVERSA,
      title: 'Nova conversa',
      description: 'Começa um chat do zero',
      icon: 'add-circle',
      aliases: ['nova', 'new', 'clear'],
    },
    {
      /**
       * Abre o palette de novo, restrito aos CLIENTES.
       *
       * ⚠️ Ação e não seção, ao contrário de Agentes e Modelo: aqueles têm meia
       * dúzia de itens e cabem na lista completa; o catálogo de clientes tem
       * centenas e afogaria as conversas. Aqui a lista é o segundo passo.
       */
      acao: ACAO_ESCOLHER_CLIENTE,
      title: 'Cliente em foco',
      description: estado.clienteTravado
        ? (estado.nomeDoClienteEmFoco || 'Geral') + ' · fixo nesta conversa'
        : estado.nomeDoClienteEmFoco || 'Escolher um cliente',
      icon: 'user-2',
      active: Boolean(estado.clienteEscopoId),
      aliases: ['cliente', 'client', 'escopo', 'foco'],
      // ⚠️ Desabilitada, e não escondida, em conversa iniciada: o cliente segue
      // valendo para as respostas, e sumir com a linha faria parecer que a
      // conversa não tem escopo. A descrição diz por que não dá para trocar.
      disabled: Boolean(estado.clienteTravado),
      // Sem catálogo carregado não há o que escolher.
      mostrar: Boolean(estado.temClientes),
    },
    {
      acao: ACAO_RACIOCINIO,
      title: 'Raciocínio',
      description: estado.raciocinioLigado ? 'Ligado' : 'Desligado',
      icon: 'mdi-brain',
      active: estado.raciocinioLigado,
      aliases: ['raciocinio', 'thinking', 'reasoning'],
      // Só existe quando o modelo em uso suporta — mesma regra do chip da barra.
      mostrar: estado.mostraRaciocinio,
    },
    {
      acao: ACAO_ANEXAR_ARQUIVOS,
      title: 'Adicionar arquivos',
      description: 'Imagem, PDF ou planilha',
      icon: 'paperclip',
      aliases: ['anexar', 'arquivo', 'attach', 'upload', 'imagem', 'foto'],
      // ★ SOME quando o anexo está travado, em vez de aparecer desabilitado.
      // O palette é busca: quem digita "anexar" e acha uma linha morta fica
      // sem saber se errou o termo ou se a função não existe. No menu do "+" a
      // escolha é a oposta (lá ela fica visível e desabilitada) porque aquela
      // é uma LISTA fixa — sumir de lá esconderia que o recurso existe.
      mostrar: estado.anexosLiberados === true,
    },
    {
      acao: ACAO_ANEXAR_CARTEIRA,
      title: 'Anexar carteira',
      description: 'Sobe o extrato do cliente para a IA ler',
      icon: 'document-upload',
      aliases: ['carteira', 'extrato', 'anexar', 'pdf', 'posicao', 'wallet'],
      // Só com o agente que sabe o que fazer com o arquivo — ver
      // `anexoDeCarteiraLiberado` no estado do palette. Some, não desabilita,
      // pela mesma razão do `anexar-arquivos` acima.
      mostrar: estado.anexoDeCarteiraLiberado === true,
    },
    {
      /**
       * Abre o modal de créditos sem sair da conversa — segunda porta para o
       * mesmo modal que o rodapé da lateral abre.
       *
       * A descrição carrega o saldo quando há número confirmado (o componente
       * formata e entrega em `estado.saldoDeCreditos`); sem ele, diz o que o
       * item faz em vez de inventar "0,00" — mesma regra do rodapé.
       */
      acao: ACAO_MEUS_CREDITOS,
      title: CREDITOS.TITULO,
      description: estado.saldoDeCreditos
        ? CREDITOS.PALETTE_SALDO(estado.saldoDeCreditos)
        : CREDITOS.PALETTE_DESCRICAO,
      icon: ICONE_CREDITOS,
      aliases: ['creditos', 'credits', 'saldo', 'carteira', 'comprar', 'wallet'],
      // Mesmo gate do rodapé (`FEATURES.AI_FRAMEWORK`, lido pelo componente):
      // sem a feature o escritório não tem créditos e o modal não faz sentido.
      mostrar: estado.mostraCreditos === true,
    },
    {
      acao: ACAO_BUSCAR_PLATAFORMA,
      title: 'Buscar na plataforma',
      description: 'Páginas, ações e clientes',
      icon: 'mdi-magnify',
      aliases: ['buscar', 'search', 'plataforma'],
    },
  ]

  const visiveis = itens.filter((item) => item.mostrar !== false)

  return filtrarNaSecao(visiveis, config.termo, SECAO_ACOES, (item) =>
    casaTexto(config.termo, item.title, ...item.aliases)
  ).map((item) => ({
    ...item,
    type: TIPO_ACAO,
    key: TIPO_ACAO + ':' + item.acao,
  }))
}

// ---------------------------------------------------------------------------
// Agentes
// ---------------------------------------------------------------------------

/**
 * O agente TRAVA junto com modelo e cliente, em conversa já iniciada.
 *
 * ⚠️ Isto reverte uma decisão anterior, e vale registrar por quê. Tecnicamente
 * escolher um agente aqui nunca foi "trocar o agente desta conversa": o
 * `irParaAgente` do `view-chat` começa uma conversa NOVA — então a ação era
 * legítima e ficava liberada, com um aviso ("nova conversa") ao lado.
 *
 * O aviso não resolve. Quem está no meio de uma conversa e escolhe outro agente
 * espera continuar ali; sair para uma conversa em branco é perder o fio, e ler
 * um selo não muda a expectativa de quem já apertou Enter. Frustração acima da
 * conveniência de pular um passo — decisão de produto de 28/08/2026.
 *
 * A porta continua existindo e uma linha acima: "Nova conversa", na seção de
 * Ações. O caminho passa a ser explícito em vez de um efeito colateral.
 *
 * @param {Object} estado o `estado` de `construirSecoes`
 */
export function tituloDosAgentes(estado) {
  const info = estado || {}
  if (!info.conversaTravada) return SECAO_AGENTES
  // Mesma frase do "Cliente em foco" travado, para os dois lerem igual.
  return SECAO_AGENTES + ' · fixo nesta conversa'
}

/**
 * Marca o agente em uso pelo `agenteAtivoId` da store — e NÃO pela persona:
 * Painel e Consultor apontam para a mesma, e derivar de volta erraria em
 * silêncio num dos dois. Vazio significa "ninguém publicou ainda", e aí nenhum
 * fica marcado, que é melhor que marcar o errado.
 */
export function secaoDeAgentes(config) {
  const estado = config.estado || {}
  const ativo = estado.agenteAtivoId
  const agentes = (config.agentes || []).filter(Boolean)

  return filtrarNaSecao(agentes, config.termo, SECAO_AGENTES, (agente) =>
    casaTexto(config.termo, agente.label, agente.description)
  ).map((agente) => ({
    type: TIPO_AGENTE,
    key: TIPO_AGENTE + ':' + agente.id,
    agenteId: agente.id,
    title: agente.label,
    description: agente.description || '',
    icon: agente.iconName || ICONE_AGENTE_PADRAO,
    active: Boolean(ativo) && agente.id === ativo,
    // Desabilitado, e não escondido — mesma escolha do modelo e do "Cliente em
    // foco": sumir com a seção faria parecer que a função deixou de existir. O
    // porquê vai no título (`tituloDosAgentes`).
    disabled: Boolean(estado.conversaTravada),
  }))
}

// ---------------------------------------------------------------------------
// Modelos
// ---------------------------------------------------------------------------

/**
 * Modelos como LINHAS, não submenu: mantém tudo buscável (`sonnet` acha o
 * modelo) e evita um segundo nível de navegação por teclado.
 *
 * `travado` desabilita as linhas em vez de sumir com a seção — conversa com
 * histórico não troca de modelo, e esconder faria parecer que a função sumiu.
 */
export function secaoDeModelos(config) {
  const estado = config.estado || {}
  const modelos = (config.modelos || []).filter(Boolean)

  return filtrarNaSecao(modelos, config.termo, SECAO_MODELO, (modelo) =>
    casaTexto(config.termo, modelo.label, modelo.id)
  ).map((modelo) => ({
    type: TIPO_MODELO,
    key: TIPO_MODELO + ':' + modelo.id,
    modeloId: modelo.id,
    title: modelo.label || modelo.id,
    description: modelo.provider || '',
    icon: ICONE_MODELO,
    active: modelo.id === estado.modeloAtivo,
    disabled: Boolean(estado.modeloTravado),
  }))
}

// ---------------------------------------------------------------------------
// Clientes
// ---------------------------------------------------------------------------

/**
 * Os clientes que dão ESCOPO à conversa — a lista do chip de cliente, trazida
 * para cá quando ele passou a abrir o palette.
 *
 * ⚠️ Sem o `TETO_VISIVEL = 8` do chip: aquele teto existia porque o painel dele
 * não rolava. Aqui rola.
 *
 * ⚠️ Casa por `casaTexto` e não pelo `filterClients` do dropdown: aquele não
 * normaliza acento, e dentro do palette o cliente se comportar diferente da
 * conversa (que normaliza) seria a inconsistência pior — "joao" tem de achar
 * "João". A consequência é que a busca de cliente difere um pouco entre a `/ia`
 * e as telas que seguem com o dropdown.
 *
 * `null` no `clienteId` é a linha "Todo o escritório": é o que o `limpar()` do
 * chip emite, e o que o backend entende por conversa sem escopo.
 */
const nomeDoCliente = (cliente) => cliente.name || cliente.nome || ''

/**
 * O que aparece sob o nome do cliente: o CÓDIGO DA CORRETORA, não o `id`.
 *
 * ★ O `id` é chave interna do banco — o assessor nunca o viu em lugar nenhum e
 * não tem como cruzá-lo com nada. O código da corretora é o identificador que
 * ele usa no cadastro, no CRM e no header do Consolidador.
 *
 * ⚠️ `formatBrokerCode` e não o campo cru: 37% dos clientes não têm código e 465
 * guardam a STRING `'None'` (o cadastro faz `str()` de um nulo, e `'None'` é
 * truthy). Sem ele, o assessor leria "None" sob o nome. Medido em produção por
 * quem escreveu o util — ver o cabeçalho dele.
 *
 * Vazio quando não há código: melhor nada que um identificador que não
 * identifica.
 */
const codigoDoCliente = (cliente) => formatBrokerCode(cliente.codigo_corretora)

/**
 * Os clientes que casam com o termo — a lista INTEIRA, sem teto.
 *
 * Exportada porque o componente precisa do total para saber se ainda há o que
 * carregar no scroll infinito. Comparação de string sobre um array em memória:
 * roda em microssegundos mesmo com milhares.
 */
export function clientesFiltrados(config) {
  const brutos = (config.clientes || []).filter(Boolean)
  if (casaSecao(config.termo, SECAO_CLIENTES)) return brutos
  return brutos.filter((cliente) =>
    // Busca também pelo CÓDIGO: é por ele que o assessor procura o cliente.
    casaTexto(config.termo, nomeDoCliente(cliente), codigoDoCliente(cliente))
  )
}

/**
 * Grupo de clientes, já com o teto de renderização e a contagem no título.
 *
 * ⚠️ Filtra o dado CRU e mapeia só a fatia que aparece. Mapear antes criava um
 * objeto por cliente do escritório a cada tecla — com algumas centenas, era o
 * que travava a navegação por seta.
 *
 * ⚠️ E o teto é só de EXIBIÇÃO: a busca continua varrendo a lista inteira, que
 * é o ponto — quem procura um cliente específico tem de achá-lo, esteja ele na
 * posição 5 ou 400. O título diz quantos ficaram de fora.
 */
export function grupoDeClientes(config) {
  const emFoco = config.estado || {}
  const idAtual = emFoco.clienteEscopoId
  // ⚠️ Conversa iniciada FIXA o cliente: o backend vincula na criação e não
  // re-vincula quem retoma. As linhas continuam visíveis (esconder faria a
  // conversa parecer sem escopo), mas nenhuma é escolhível.
  const travado = Boolean(emFoco.clienteTravado)

  const casados = clientesFiltrados(config)
  // Quantos o componente já liberou — sobe a cada leva do scroll infinito.
  const limite = config.limiteDeClientes || CLIENTES_POR_LEVA

  const itens = casados.slice(0, limite).map((cliente) => ({
    type: TIPO_CLIENTE,
    key: TIPO_CLIENTE + ':' + cliente.id,
    clienteId: cliente.id,
    disabled: travado,
    // O mesmo `name || nome` do rótulo do chip: as duas fontes de cliente da
    // plataforma não concordam no nome do campo.
    title: nomeDoCliente(cliente),
    description: codigoDoCliente(cliente),
    icon: ICONE_CLIENTE,
    active: Boolean(idAtual) && String(cliente.id) === String(idAtual),
  }))

  // "Todo o escritório" entra em repouso, quando o termo nomeia a seção, ou
  // quando casa com ela própria. Com filtro de nome, ficar sempre no topo
  // empurraria para baixo o cliente que se está procurando.
  const mostraGeral =
    !texto(config.termo) ||
    casaSecao(config.termo, SECAO_CLIENTES) ||
    casaTexto(config.termo, ESCOPO_GERAL, 'geral', 'escritório')

  if (!mostraGeral && !itens.length) return null

  const geral = {
    type: TIPO_CLIENTE,
    key: TIPO_CLIENTE + ':geral',
    clienteId: null,
    title: ESCOPO_GERAL,
    description: travado
      ? 'Fixo nesta conversa'
      : 'Conversa sem cliente em foco',
    icon: ICONE_CLIENTE,
    active: !idAtual,
    disabled: travado,
  }

  return {
    title: tituloDosClientes(itens.length, casados.length),
    items: mostraGeral ? [geral].concat(itens) : itens,
  }
}

/**
 * "Clientes (30 de 412)" quando corta, "Clientes (12)" quando cabe tudo.
 *
 * Cortar em silêncio é o defeito que este palette já cometeu com as conversas
 * ("oito de trinta sem pista de que existem trinta"). Aqui o número também
 * ensina o caminho: quem vê 30 de 412 entende que precisa digitar.
 */
function tituloDosClientes(exibidos, total) {
  if (exibidos >= total) return tituloComContagem(SECAO_CLIENTES, total)
  return SECAO_CLIENTES + ' (' + exibidos + ' de ' + total + ')'
}

// ---------------------------------------------------------------------------
// Conversas
// ---------------------------------------------------------------------------

/**
 * Data + agente, nessa ordem — a mesma composição do `metaLabel` da linha da
 * lateral (`conversation-row.vue`). Sem a data, uma lista de "O que preciso
 * fazer hoje?" repetido não distingue nada: o agente é igual em todas.
 */
const metaDaConversa = (conversa, rotulos) =>
  [formatConversationDate(conversa.updated_at), rotulos[conversa.context]]
    .filter(Boolean)
    .join(SEPARADOR_META)

/**
 * O cliente vira CHIP, e não texto na meta: é o escopo que a conversa fixa ao
 * ser aberta (ver `clienteDaConversa`), então é estado, não descrição. Conversa
 * geral não tem chip — `id_cliente_app` vem nulo, e é o normal.
 *
 * ⚠️ Pede ÍCONE, não cor. Com `color` o item renderiza um `BaseStatus` tingido,
 * que é o vocabulário de estado de PROCESSO (aprovado, pendente) — nomear uma
 * entidade ali dava ao selo mais peso que o próprio título da conversa. Com
 * `icon` ele cai no chip cinza + `user-2` na cor da marca, o mesmo desenho do
 * cartão de escopo e do chip de escopo no composer.
 *
 * `codigo_cliente` é o fallback: as duas colunas vêm em toda linha, mas o nome
 * pode faltar em base antiga.
 */
const chipDoCliente = (conversa) => {
  const nome = conversa.nome_cliente || conversa.codigo_cliente
  if (!conversa.id_cliente_app || !nome) return []
  return [{ label: String(nome), icon: ICONE_CLIENTE }]
}

const entradaDeConversa = (conversa, rotulos) => ({
  type: TIPO_CONVERSA,
  key: TIPO_CONVERSA + ':' + conversa.id,
  conversaId: conversa.id,
  title: conversa.topic || CONVERSA_SEM_TITULO,
  description: metaDaConversa(conversa, rotulos),
  badges: chipDoCliente(conversa),
  icon: ICONE_CONVERSA,
})

/**
 * Casa também por CLIENTE, não só por título.
 *
 * `filtrarPorTitulo` (de `agrupar-conversas`) normaliza acento e é o helper que
 * a lateral da rota cheia já usa — reusado para as duas buscas casarem. O
 * cliente entra por fora porque aquele helper só olha `topic`, e
 * `nome_cliente`/`codigo_cliente` vêm em toda linha desde 23/08/2026.
 *
 * ⚠️ Ponto único de troca: quando o backend ganhar o `q` (hoje o GET não tem
 * parâmetro de busca), é esta função que muda — e só ela.
 */
export function filtrarConversas(conversas, termo) {
  const lista = conversas || []
  const alvo = texto(termo)
  if (!alvo) return lista.slice()
  // "conversas" / "histórico" nomeiam a seção: devolve tudo, em vez de procurar
  // a palavra dentro dos títulos.
  if (casaSecao(alvo, SECAO_CONVERSAS)) return lista.slice()

  const porTitulo = filtrarPorTitulo(lista, alvo)
  const vistas = new Set(porTitulo.map((conversa) => conversa.id))
  const porCliente = lista.filter(
    (conversa) =>
      conversa &&
      !vistas.has(conversa.id) &&
      casaTexto(alvo, conversa.nome_cliente, conversa.codigo_cliente)
  )
  return porTitulo.concat(porCliente)
}

/**
 * Com termo digitado a lista sai PLANA e inteira — quem busca quer o resultado,
 * não a arrumação. Sem termo, agrupa (Pastas ou Agente) como a lateral da rota
 * cheia, que é justamente o que faltava aqui.
 */
export function secoesDeConversas(config) {
  const estado = config.estado || {}
  const conversas = filtrarConversas(config.conversas, config.termo)
  const rotulos = indexarRotulosPorPersona(config.agentes)

  if (texto(config.termo)) {
    if (!conversas.length) return []
    return [
      {
        title: tituloComContagem(SECAO_CONVERSAS, conversas.length),
        items: conversas.map((conversa) =>
          entradaDeConversa(conversa, rotulos)
        ),
      },
    ]
  }

  const modo = estado.modoAgrupamento || MODO_AGRUPAMENTO_PASTAS
  const grupos = agruparConversas(conversas, modo, {
    pastas: config.pastas || [],
  })

  return (
    grupos
      // ⚠️ Pasta VAZIA sai daqui, ao contrário da lateral — lá ela é alvo de
      // drag-and-drop e precisa existir para se arrastar de volta; aqui não há
      // arrastar, e o cabeçalho sem nada embaixo virava uma pilha de títulos
      // ("789 (0)", "CINEMA (0)", "TESTE (0)") empurrando as conversas para
      // fora da primeira tela.
      .filter((grupo) => grupo.itens.length)
      .map((grupo) => ({
        title: tituloComContagem(grupo.titulo, grupo.itens.length),
        items: grupo.itens.map((conversa) =>
          entradaDeConversa(conversa, rotulos)
        ),
      }))
  )
}

/**
 * O contador que a lateral tem no "Ver todas (N)" e que faltava aqui — era o
 * que fazia o palette mostrar oito de trinta sem pista de que existiam trinta.
 */
export function tituloComContagem(titulo, total) {
  return titulo + ' (' + total + ')'
}

// ---------------------------------------------------------------------------
// Composição
// ---------------------------------------------------------------------------

const comItens = (grupos, title, items) => {
  if (items.length) grupos.push({ title, items })
  return grupos
}

/**
 * Palette restrito a UMA seção — quando quem abriu foi um chip do composer.
 *
 * O chip de agente e o de cliente deixaram de ter dropdown próprio na `/ia`:
 * eles abrem este modal já filtrado no que o dropdown mostraria. Some com o
 * resto porque a pergunta ali é "qual agente?", não "o que você quer fazer?".
 */
function secoesDoEscopo(escopo, opcoes) {
  const grupos = []
  if (escopo === ESCOPO_AGENTES) {
    comItens(
      grupos,
      tituloDosAgentes(opcoes.estado),
      secaoDeAgentes(opcoes)
    )
  }
  if (escopo === ESCOPO_MODELOS) {
    comItens(grupos, SECAO_MODELO, secaoDeModelos(opcoes))
  }
  if (escopo === ESCOPO_CLIENTES) {
    // Grupo pronto (com a contagem no título), e não `comItens`: só ele sabe
    // quantos ficaram de fora do teto.
    const clientes = grupoDeClientes(opcoes)
    if (clientes) grupos.push(clientes)
  }
  return grupos
}

/**
 * @param {Object} config
 *   `termo`             o que foi digitado ('' = estado de repouso)
 *   `escopo`            `ESCOPO_AGENTES` / `ESCOPO_CLIENTES` / `ESCOPO_MODELOS`,
 *                       ou vazio (tudo)
 *   `agentes`           catálogo JÁ filtrado por feature flag (`agentesVisiveis`)
 *   `modelos`           `supportedModels` da store
 *   `conversas`         as carregadas na store do chat
 *   `pastas`            `folders` da store
 *   `clientes`          catálogo de clientes do escritório (`user/allClients`)
 *   `estado`            `{ modeloAtivo, modeloTravado, clienteTravado,
 *                          nomeDoClienteEmFoco, temClientes, raciocinioLigado,
 *                          mostraRaciocinio, modoAgrupamento, agenteAtivoId,
 *                          clienteEscopoId, anexosLiberados, mostraCreditos,
 *                          saldoDeCreditos }`
 * @returns {Array} `[{ title, items }]`, sem grupo vazio
 */
export function construirSecoes(config) {
  const opcoes = config || {}
  if (opcoes.escopo) return secoesDoEscopo(opcoes.escopo, opcoes)

  const grupos = []
  comItens(grupos, SECAO_ACOES, secaoDeAcoes(opcoes))
  comItens(grupos, tituloDosAgentes(opcoes.estado), secaoDeAgentes(opcoes))
  comItens(grupos, SECAO_MODELO, secaoDeModelos(opcoes))

  return grupos.concat(secoesDeConversas(opcoes))
}
